import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useModal } from '../context/ModalContext';
import { useContent } from '../context/ContentContext';
import { Phone, Mail, MapPin, ArrowUp } from 'lucide-react';
import { getMenuConfig } from '../lib/menuConfig';

const Footer = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { openModal } = useModal();
  const { siteContent } = useContent();
  const navigate = useNavigate();
  const isRtl = language === 'ar';
  const currentYear = new Date().getFullYear();
  const menuConfig = getMenuConfig(siteContent?.global?.menu);

  const logoUrl = isDark
    ? (siteContent?.global?.logo_url_dark || '/uploads/f0ff146b-d9ae-4c6c-b8cb-08a79738ee50.png')
    : (siteContent?.global?.logo_url_light || '/uploads/a76604b9-11dd-4737-a8b8-00479d98f42a.png');

  const phoneNumber = t('contact.phone.number');
  const emailAddress = t('contact.email.address');
  const officeAddress = t('contact.office.address');
  const officeCity = t('contact.office.city');

  const navItems = [
    { key: 'home', href: '#home', popup: null },
    { key: 'about', href: '#about', popup: 'about' },
    { key: 'products', href: '#products', popup: 'products' },
    { key: 'news', href: '/news', popup: null, route: true },
    { key: 'contact', href: '#contact', popup: 'contact' },
  ].filter(item => menuConfig[item.key]?.enabled !== false);

  const handleNavClick = (item) => {
    if (item.popup) {
      openModal('section', { section: item.popup });
    } else if (item.route) {
      navigate(item.href);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer data-testid="footer" className={`relative ${isDark ? 'bg-[#050505]' : 'bg-[#F5F0E8]'}`}>
      <div className="h-1 bg-gradient-to-r from-transparent via-[var(--gold-primary)] to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div className={`lg:col-span-1 ${isRtl ? 'text-right' : 'text-left'}`}>
            <div className={`flex items-center gap-3 mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <img src={logoUrl} alt="Royal Tobacco" className="h-16 sm:h-[4.5rem] w-auto" />
              <div>
                <span className="font-display text-xl font-bold gold-text block leading-tight">ROYAL</span>
                <span className={`text-[10px] tracking-[0.3em] uppercase ${isDark ? 'text-white/50' : 'text-gray-500'}`}>TOBACCO</span>
              </div>
            </div>
            <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              {t('footer.tagline')}
            </p>
            <div className={`flex items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
              {['twitter', 'instagram', 'linkedin', 'facebook'].map(social => (
                <button key={social} onClick={() => openModal('section', { section: 'social' })} className={`w-9 h-9 flex items-center justify-center rounded-sm hover:bg-[var(--gold-primary)] hover:text-black transition-all duration-300 ${isDark ? 'bg-white/5 text-white/50' : 'bg-gray-200 text-gray-500'}`}>
                  {social === 'twitter' && <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>}
                  {social === 'instagram' && <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>}
                  {social === 'linkedin' && <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>}
                  {social === 'facebook' && <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg>}
                </button>
              ))}
            </div>
          </div>

          <div className={isRtl ? 'text-right' : 'text-left'}>
            <h4 className={`font-display text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>{language === 'ar' ? 'روابط سريعة' : 'Quick Links'}</h4>
            <nav className="flex flex-col gap-3">
              {navItems.map(item => (
                <a key={item.key} href={item.href} onClick={e => { e.preventDefault(); handleNavClick(item); }} className={`hover:text-[var(--gold-primary)] transition-colors text-sm inline-flex items-center gap-2 group ${isRtl ? 'flex-row-reverse' : ''} ${isDark ? 'text-white/60' : 'text-gray-600'}`} data-testid={`footer-nav-${item.key}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold-primary)]/50 group-hover:bg-[var(--gold-primary)] transition-colors" />
                  {t(`nav.${item.key}`)}
                </a>
              ))}
            </nav>
          </div>

          <div className={isRtl ? 'text-right' : 'text-left'}>
            <h4 className={`font-display text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>{language === 'ar' ? 'معلومات الاتصال' : 'Contact Info'}</h4>
            <div className="space-y-4">
              <div className={`flex items-start gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <MapPin className="w-4 h-4 text-[var(--gold-primary)] mt-1 flex-shrink-0" />
                <div className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                  <p>{officeAddress}</p>
                  <p>{officeCity}</p>
                </div>
              </div>
              <a href={`tel:${phoneNumber.replace(/[^+0-9]/g, '')}`} className={`flex items-center gap-3 text-sm hover:text-[var(--gold-primary)] transition-colors ${isRtl ? 'flex-row-reverse' : ''} ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                <Phone className="w-4 h-4 text-[var(--gold-primary)]" /><span dir="ltr">{phoneNumber}</span>
              </a>
              <a href={`mailto:${emailAddress}`} className={`flex items-center gap-3 text-sm hover:text-[var(--gold-primary)] transition-colors ${isRtl ? 'flex-row-reverse' : ''} ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                <Mail className="w-4 h-4 text-[var(--gold-primary)]" /><span dir="ltr">{emailAddress}</span>
              </a>
            </div>
          </div>

          <div className={isRtl ? 'text-right' : 'text-left'}>
            <h4 className={`font-display text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>{language === 'ar' ? 'ابق على تواصل' : 'Stay Connected'}</h4>
            <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              {language === 'ar' ? 'تابعنا للحصول على آخر الأخبار والتحديثات من رويال للتبغ.' : 'Follow us for the latest news and updates from Royal Tobacco.'}
            </p>
            {menuConfig.contact?.enabled !== false && (
              <button onClick={() => openModal('section', { section: 'contact' })} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--gold-primary)] text-black font-semibold text-xs uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-colors">
                {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-300'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${isRtl ? 'sm:flex-row-reverse' : ''}`}>
            <p className={`text-xs text-center ${isRtl ? 'sm:text-right' : 'sm:text-left'} ${isDark ? 'text-white/50' : 'text-gray-500'}`}>© {currentYear} Royal Tobacco. {t('footer.rights')}</p>
            <div className={`flex items-center gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <button onClick={scrollToTop} className="w-9 h-9 flex items-center justify-center rounded-sm bg-[var(--gold-primary)] text-black hover:bg-[var(--gold-hover)] transition-colors" aria-label="Back to top">
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
